import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Mail,
  Phone,
  Clock,
  CheckCircle,
  Inbox,
  BadgeDollarSign,
  Handshake,
  LifeBuoy,
  Newspaper,
} from 'lucide-react';
import HeroSection from '../components/HeroSection';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const contacts = [
  { dept: 'General Inquiries', email: 'accounting@bizfund.com', icon: Inbox },
  { dept: 'Apply for Funding', email: 'funding@bizfund.com', icon: BadgeDollarSign },
  { dept: 'Merchant Support', email: 'accounting@bizfund.com', icon: LifeBuoy },
  { dept: 'Partner and ISO Program', email: 'partner@bizfund.com', icon: Handshake },
  { dept: 'Media and Press', email: 'funding@bizfund.com', icon: Newspaper },
];

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handle = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();

    if (!form.name || !form.email || !form.message) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const response = await fetch('/api/contact-message', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(form),
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.error || 'Unable to send your message right now.');
      }

      setSubmitted(true);
    } catch (error) {
      console.error('Contact form submission failed:', error);
      setSubmitError('We were unable to send your message at this time. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass =
    'w-full px-4 py-3 rounded-2xl border border-gray-200 bg-white text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 transition-shadow shadow-[0_2px_10px_rgba(15,23,42,0.03)]';

  return (
    <main className="bg-[#F8F7F4] text-gray-900">
      <HeroSection
        tag="Get In Touch"
        title="We're Here to Support<br/>Your Business"
        subtitle="Questions, partnership or applications - Our team is here to help"
        image="/images/offline/photo-1553877522-43269d4ea984.jpg"
      />

      <section className="page-section bg-[#F8F7F4]">
        <div className="page-container">
          <div className="grid gap-8 lg:grid-cols-5 lg:gap-10 xl:gap-12">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="space-y-6 lg:col-span-2 md:space-y-8"
            >
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-6">Contact by Department</h3>
                <div className="space-y-4">
                  {contacts.map((c) => {
                    const Icon = c.icon;

                    return (
                      <div
                        key={c.dept}
                        className="flex items-start gap-4 rounded-[24px] border border-gray-200 bg-white p-4 shadow-[0_10px_30px_rgba(15,23,42,0.06)] md:p-5"
                      >
                        <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-[#0F172A] text-amber-400 flex-shrink-0">
                          <Icon size={20} />
                        </div>
                        <div>
                          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
                            {c.dept}
                          </div>
                          <a
                            href={`mailto:${c.email}`}
                            className="text-amber-700 font-medium text-sm hover:text-amber-800 hover:underline"
                          >
                            {c.email}
                          </a>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-4 rounded-[24px] border border-gray-200 bg-white p-5 shadow-[0_10px_30px_rgba(15,23,42,0.06)] md:p-6">
                <h4 className="font-bold text-gray-900">Office Hours</h4>

                <div className="flex items-center gap-3 text-sm text-gray-600">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-[#0F172A] flex-shrink-0">
                    <Clock size={16} className="text-amber-400" />
                  </span>
                  Mon–Fri: 9:00 AM – 6:00 PM EST
                </div>

                <div className="flex items-center gap-3 text-sm text-gray-600">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-[#0F172A] flex-shrink-0">
                    <Phone size={16} className="text-amber-400" />
                  </span>
                  +1 (888) 958-3781
                </div>

                <div className="flex items-center gap-3 text-sm text-gray-600">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-[#0F172A] flex-shrink-0">
                    <Mail size={16} className="text-amber-400" />
                  </span>
                  Avg. response: under 2 hours
                </div>
              </div>
            </motion.div>

            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="lg:col-span-3"
            >
              <div className="rounded-[32px] border border-gray-200 bg-white p-6 shadow-[0_14px_40px_rgba(15,23,42,0.07)] sm:p-8 md:p-10">
                {!submitted ? (
                  <>
                    <h3 className="mb-5 text-2xl font-bold text-gray-900 md:mb-6">Send Us a Message</h3>

                    <form onSubmit={submit} className="space-y-5">
                      <div className="grid gap-4 sm:grid-cols-2 md:gap-5">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name *</label>
                          <input
                            name="name"
                            value={form.name}
                            onChange={handle}
                            required
                            placeholder="Jane Smith"
                            className={inputClass}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address *</label>
                          <input
                            name="email"
                            type="email"
                            value={form.email}
                            onChange={handle}
                            required
                            placeholder="jane@company.com"
                            className={inputClass}
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Subject</label>
                        <input
                          name="subject"
                          value={form.subject}
                          onChange={handle}
                          placeholder="How can we help you?"
                          className={inputClass}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Message *</label>
                        <textarea
                          name="message"
                          value={form.message}
                          onChange={handle}
                          required
                          rows={5}
                          placeholder="Tell us about your business and how we can help..."
                          className={`${inputClass} resize-none`}
                        />
                      </div>

                      {submitError && (
                        <p className="text-sm text-red-600">{submitError}</p>
                      )}

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-[#0F172A] hover:bg-[#111c33] disabled:cursor-not-allowed disabled:opacity-60 text-white font-semibold py-3.5 rounded-full transition-all duration-300 shadow-[0_12px_28px_rgba(15,23,42,0.14)]"
                      >
                        {isSubmitting ? 'Sending...' : 'Send Message'}
                      </button>
                    </form>
                  </>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-16"
                  >
                    <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-5 ring-1 ring-emerald-100">
                      <CheckCircle size={32} className="text-emerald-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Message Sent!</h3>
                    <p className="text-gray-500 leading-relaxed max-w-sm mx-auto">
                      Thanks, {form.name}. We'll get back to you at <strong>{form.email}</strong> within 2 business hours.
                    </p>
                  </motion.div>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
}
